import { Either } from 'monet';
import LeftResponse from '../LeftResponse';
import {
  makeDentalFormulaTeeth, makeServiceTableByScheme, makeChiefStatusBySchema, makeDiaryByScheme,
} from './VisitGateway';

/**
 * @description Информация о визите.
 * @param {number} id           Идентификатор визита.
 * @param {string} date         Дата визита.
 * @param {number} employeeId   Идентификатор сотрудника.
 * @param {string} employeeName Имя сотрудника.
 * @param {number} patientId    Идентификатор пациента.
 * @param {string} patientName  Имя пациента.
 * @param {string} time         Время визита
 * @param {string} title        Заголовок.
 * @param {boolean} sanitized   Санирован.
 */
function VisitInfo(
  id,
  date,
  employeeId,
  employeeName,
  patientId,
  patientName,
  time,
  title,
  sanitized,
) {
  this.id = id;
  this.date = date;
  this.employeeId = employeeId;
  this.employeeName = employeeName;
  this.patientId = patientId;
  this.patientName = patientName;
  this.time = time;
  this.title = title;
  this.sanitized = sanitized;
}

/**
 * @description Создать информацию о визите.
 * @param {Object} raw Сырые данные.
 * @returns {VisitInfo}
 */
const makeVisitInfo = (raw) => new VisitInfo(
  raw.id,
  raw.date,
  raw.employee_id,
  raw.employee_name,
  raw.patient_id,
  raw.patient_name,
  raw.time,
  raw.title,
  raw.sanitized,
);

/**
 * @description Шлюз главного врача.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function ChiefPhysicianGateway(axios) {
  /**
   * @description Отменить подпись СЭМД протокола консультации.
   * @param {string} documentId Идентификатор документа.
   * @returns {Promise<Either>}
   */
  this.cancelCdaConsultationProtocolSignature = (documentId) => axios
    .put(`/api/cda-consultation-protocol-documents/${documentId}/signature-cancellation`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Проверить заполненность визита для СЭМД протокола консультации.
   * @param {string} visitId Идентификатор визита.
   * @returns {Promise<Either>}
   */
  this.checkCdaConsultationProtocolForVisit = (visitId) => axios
    .get(`/api/chief-physician/visits/${visitId}/consultation-protocol-fullness`)
    .then(
      (response) => {
        const { status } = response.data;
        if (status === 'ok') {
          return Either.of(response.data);
        }
        return Either.Left(response.data);
      },
      (error) => {
        if (error.isAxiosError && error.response.status === 404) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить подпись СЭМД протокола консультации.
   * @param {string} documentId Идентификатор документа.
   * @param {string} hash       Хэш содержимого.
   * @param {string} signature  Подпись ЭЦП.
   * @returns {Promise<Either>}
   */
  this.addCdaConsultationProtocolSignature = (documentId, hash, signature) => axios
    .post(
      `/api/cda-consultation-protocol-documents/${documentId}/signatures`,
      {
        hash,
        signature,
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400 || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Выгрузить СЭМД протокола консультации на подпись.
   * @param {string} visitId                  Идентификатор визита.
   * @param {string} selectedDoctorPosition   Выбранная должность доктора.
   * @param {string} selectedDoctorSpeciality Выбранная специальность доктора.
   * @returns {Promise<Either>}
   */
  this.uploadCdaConsulatationProtocolForSigning = (
    visitId,
    selectedDoctorPosition,
    selectedDoctorSpeciality,
  ) => axios
    .post(
      `/api/chief-physician/visits/${visitId}/consultation-protocol`,
      {
        doctor_position_id: selectedDoctorPosition,
        doctor_speciality_id: selectedDoctorSpeciality,
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400 || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список визитов на проверку.
   * @param {Object} query Запрос.
   * @returns {Promise<Either>}
   */
  this.viewList = (query) => axios
    .get('/api/chief-physician/visits', { params: query })
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить опции для списка визитов на проверку.
   * @param {Object} query Запрос.
   * @returns {Promise<Either>}
   */
  this.viewVisitListOptions = (query) => axios
    .get('/api/chief-physician/visit-options', { params: query })
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить визит по идентификатору.
   * @param {number} visitId Идентификатор визита.
   * @returns {Promise<Either>}
   */
  this.viewById = (visitId) => axios.get(`/api/chief-physician/visits/${visitId}`).then(
    async (response) => {
      const { data } = response.data;
      const dentalFormula = makeDentalFormulaTeeth(data.dental_formula);
      const diaries = (data.diaries || []).map(makeDiaryByScheme);
      const explanationErrorColumns = data.explanation_error_columns;
      const explanationErrorDescription = data.explanation_error_description;
      const explanationErrorRows = data.explanation_error_rows;
      const review = makeChiefStatusBySchema(data.review);
      const services = makeServiceTableByScheme(data.services);
      const visitInfo = makeVisitInfo(data.visit);
      return Either.right({
        actions: data.actions,
        dentalFormula,
        discountType: data.discount_type,
        diaries,
        explanationErrorColumns,
        explanationErrorDescription,
        explanationErrorRows,
        review,
        services,
        visitInfo,
      });
    },
    async (error) => {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        const { causes, title } = error.response.data;
        const leftResponse = new LeftResponse(causes, title);
        return Either.left(leftResponse);
      }
      throw error;
    },
  );

  /**
   * @description Отправить повторно СЭМД протокола консультации.
   * @param {string} documentId Идентификатор документа.
   * @returns {Promise<Either>}
   */
  this.sendCdaConsultationProtocolAgain = (documentId) => axios
    .post(`/api/cda-consultation-protocol-documents/${documentId}/again-sendings`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400 || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить СЭМД протокола консультации.
   * @param {string} documentId Идентификатор документа.
   * @returns {Promise<Either>}
   */
  this.sendCdaConsultationProtocol = (documentId) => axios
    .post(`/api/cda-consultation-protocol-documents/${documentId}/sendings`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400 || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить визит на доработку.
   * @param {number} visitId Идентификатор визита.
   * @param {string} note Примечание.
   * @returns {Promise<Either>}
   */
  this.sendVisitForRevision = (visitId, note) => axios
    .put(`/api/chief-physician/visits/${visitId}/rejected`, { note })
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
